import { action, observable } from 'mobx';

import { EAjaxFunctions, EAppsGenreEng, EResponseStatus } from '@enums';

import { Log } from '@utils';

import { getAdaptedApps } from '@stores';

import { Api } from '@api';

import { IAppData } from '@interfaces';
import { ICGIUrlOptions } from '@interfaces/utils';

import { AppsByGenreCompilationType, IAppsByGenre, IAppsByGenreStore } from './types';

export class AppsByGenreStore {

	// Одна игра  может находиться в подборках нескольких жанров.
	// Чтобы, при отображении подборок на одном экране, игры во вьюпорте не повторялись,
	// при загрузке новой подборки, я смотрю на уже загруженные и
	// двигаю в новой подборке игры в конец списка.
	// isNeedUniqAppsFirst - указывает нужно ли это делать
	constructor(isNeedUniqAppsFirst?: boolean) {
		this.isNeedUniqAppsFirst = isNeedUniqAppsFirst;
	}

	isNeedUniqAppsFirst = false;

	// подборка игр по жанрам
	// объект, где ключ - жанр, а свойство - IAppData
	@observable
	appsByGenreCompilation: AppsByGenreCompilationType = null;

	// Массив с уже ID игр, уже хранящихся в сторе
	@observable
	appsIds: string[] = [];

	// Получить подборку по жанру
	getByGenre(chosenGenre: EAppsGenreEng): IAppsByGenre {
		if (this.appsByGenreCompilation && this.appsByGenreCompilation[chosenGenre]) {
			return this.appsByGenreCompilation[chosenGenre];
		} else {
			return null;
		}
	};

	// загрузить игры и заменить ими игры в сторе
	@action
	async fetch(genre: EAppsGenreEng, options: ICGIUrlOptions): Promise<void> {
		this.clearGenre(genre);
		const responseData = await this.fetchData({ ...options, genre });

		this.appsByGenreCompilation = {
			...this.appsByGenreCompilation,
			[genre]: (responseData?.status === EResponseStatus.OK && this.isNeedUniqAppsFirst)
				? this.getSortedCompilationUniqueFirst(responseData)
				: responseData,
		};
	};

	// загрузить игры с сервера и добавить к уже имеющимся
	@action
	async fetchNext(genre: EAppsGenreEng, options: ICGIUrlOptions): Promise<void> {
		const responseData = await this.fetchData({ ...options, genre });
		const store = this.appsByGenreCompilation[genre];

		this.appsByGenreCompilation[genre] = {
			...responseData,
			data: [...store.data, ...responseData.data],
			status: responseData.status,
			// total не меняется - это общее кол-во игр
			total: store.total || null,
		};
	};

	// очистить весь стор игр по жанру
	@action
	clearStore() {
		this.appsByGenreCompilation = null;
	}

	// очистить подборку из стора
	@action
	clearGenre(genre: EAppsGenreEng) {
		if (this.appsByGenreCompilation && this.appsByGenreCompilation[genre]) {
			this.appsByGenreCompilation[genre].data = [];
			this.changeCompilationStatus(genre, null);
		}
	}

	async fetchData(options: ICGIUrlOptions) {
		let result: IAppsByGenre = {
			data: [],
			status: EResponseStatus.PENDING,
		};

		try {
			const response = await Api.apps.fetchApps(EAjaxFunctions.APPS_GENRE, options);

			if (response) {
				const { apps: data, ...rest } = getAdaptedApps(response);

				result = {
					...rest,
					data,
					status: EResponseStatus.OK,
				};
			}
		} catch (err) {
			Log.error(`Failed in getting apps in AppsStore; Error:`, err);
			result.status = EResponseStatus.ERROR;
		}

		return result;
	}

	// отсортировать подборку, таким образом,
	// чтобы игры, id которых имеется в idList[] (либо в this.appsIds[]) падали вниз списка
	@action
	sortAppsByUniqueFirst(genre: EAppsGenreEng, idList?: string[]): void {
		this.appsByGenreCompilation[genre] = this.getSortedCompilationUniqueFirst(this.appsByGenreCompilation[genre], idList);
	}

	// получить отсортированную подборку, таким образом,
	// чтобы уже имеющиемся в сторе в других подборках игры падали вниз списка
	// idList - список id, которые должны попасть в конце подборки,
	// в случае отсутствиия этого списка, сортируется по this.appsIds (id из игр по уже загруженный игр по жанру)
	getSortedCompilationUniqueFirst(compilation: IAppsByGenre, idList?: string[]) {
		const appsByGenreStart: IAppData[] = [];
		const appsByGenreEnd: IAppData[] = [];
		const appsIds = idList || this.appsIds;

		if (compilation?.data) {
			compilation?.data?.forEach((app: IAppData) => {
				const id = app.id;

				if (!appsIds.includes(id)) {
					!idList && this.appsIds.push(id);
					appsByGenreStart.push(app);
				} else {
					appsByGenreEnd.push(app);
				}
			});

			return {
				...compilation,
				data: [...appsByGenreStart, ...appsByGenreEnd],
			};
		} else {
			Log.warn(`Couldn't sort the apps by genre. Array of games is empty!`);
		}
	}

	protected changeCompilationStatus(genre: EAppsGenreEng, status: EResponseStatus) {
		if (this.appsByGenreCompilation && this.appsByGenreCompilation[genre]) {
			this.appsByGenreCompilation[genre].status = status;
		} else {
			throw Error(`Can't change status for compilation by genre "${genre}" in status "${status}".`);
		}
	}

	toJSON() {
		return {
			appsByGenreCompilation: this.appsByGenreCompilation,
		};
	}

	fromJSON(data: IAppsByGenreStore) {
		this.appsByGenreCompilation = { ...this.appsByGenreCompilation, ...data.appsByGenreCompilation };
	}

}
